/**
 * Mridu Kaya storefront behaviour.
 * 1. Section reveals, staggered a child at a time.
 * 2. The block-print ornament, drawn as real strokes instead of a printed fill.
 * Re-runs on shopify:section:load so the theme editor stays live.
 */
(function () {
  var NS = 'http://www.w3.org/2000/svg';
  var reduceMotion = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  function node(name, attrs) {
    var el = document.createElementNS(NS, name);
    for (var key in attrs) el.setAttribute(key, attrs[key]);
    return el;
  }

  function drawSurface(width, height) {
    var svg = node('svg', { viewBox: '0 0 ' + width + ' ' + height, preserveAspectRatio: 'none' });
    svg.setAttribute('class', 'ink-draw');
    svg.setAttribute('aria-hidden', 'true');
    return svg;
  }

  /* A border of stacked registers: two freehand rules over a row of combed fans. */
  function register(width) {
    var svg = drawSurface(width, 26);
    var tiles = Math.ceil(width / 40) + 1;

    svg.appendChild(node('path', { d: 'M0 0.6 H' + width, 'stroke-width': 1.1 }));
    svg.appendChild(node('path', { d: 'M0 25.4 H' + width, 'stroke-width': 1.1 }));

    for (var i = 0; i < tiles; i++) {
      var x = i * 40;
      svg.appendChild(node('rect', { x: x + 4, y: 4, width: 3.4, height: 3.4 }));
      svg.appendChild(node('rect', { x: x + 24, y: 4, width: 3.4, height: 3.4 }));

      [10, 30].forEach(function (offset) {
        var cx = x + offset;
        svg.appendChild(
          node('path', {
            d: 'M' + (cx - 6.6) + ' 22.6 A6.6 6.6 0 0 1 ' + (cx + 6.6) + ' 22.6',
            'stroke-width': 1,
          })
        );
        svg.appendChild(
          node('path', {
            d: 'M' + cx + ' 22.6 V16 M' + cx + ' 22.6 L' + (cx - 4.4) + ' 18.2 M' + cx + ' 22.6 L' + (cx + 4.4) + ' 18.2',
            'stroke-width': 1,
          })
        );
        svg.appendChild(node('circle', { cx: cx, cy: 21.4, r: 1 }));
      });
    }
    return svg;
  }

  /* The lozenge lattice: double contour, centre bindu, a cross in every gap. */
  function lattice(width, height) {
    var tile = 84;
    var cols = Math.ceil(width / tile) + 1;
    var rows = Math.ceil(height / tile) + 1;
    var svg = drawSurface(width, height);

    for (var r = 0; r < rows; r++) {
      for (var c = 0; c < cols; c++) {
        var x = c * tile;
        var y = r * tile;
        var cx = x + 42;
        var cy = y + 42;
        var wave = r + c;

        svg.appendChild(
          node('path', {
            d: 'M' + cx + ' ' + (y + 7) + 'L' + (x + 77) + ' ' + cy + 'L' + cx + ' ' + (y + 77) + 'L' + (x + 7) + ' ' + cy + 'Z',
            'stroke-width': 1.15,
            'data-wave': wave,
          })
        );
        svg.appendChild(
          node('path', {
            d: 'M' + cx + ' ' + (y + 16) + 'L' + (x + 68) + ' ' + cy + 'L' + cx + ' ' + (y + 68) + 'L' + (x + 16) + ' ' + cy + 'Z',
            'stroke-width': 1.05,
            'data-wave': wave,
          })
        );
        svg.appendChild(
          node('path', {
            d: 'M' + x + ' ' + (y + 4) + 'V' + (y - 4) + 'M' + (x - 4) + ' ' + y + 'H' + (x + 4),
            'stroke-width': 1,
            'data-wave': wave,
          })
        );

        var bindu = node('circle', { cx: cx, cy: cy, r: 3 });
        bindu.setAttribute('data-wave', wave);
        svg.appendChild(bindu);
      }
    }
    return svg;
  }

  function prime(svg, step, cap) {
    [].forEach.call(svg.querySelectorAll('path'), function (path, index) {
      var length = 200;
      try {
        length = path.getTotalLength();
      } catch (error) {
        /* geometry unavailable: fall back to a plausible dash length */
      }
      var wave = path.getAttribute('data-wave');
      var order = wave === null ? index : Number(wave);

      path.style.setProperty('--len', length.toFixed(1));
      path.style.setProperty('--dur', (length > 400 ? 1.1 : 0.55) + 's');
      path.style.transitionDelay = Math.min(order * step, cap) + 'ms';
    });

    [].forEach.call(svg.querySelectorAll('rect, circle'), function (shape, index) {
      var wave = shape.getAttribute('data-wave');
      var order = wave === null ? index : Number(wave);
      shape.style.transitionDelay = Math.min(order * step, cap) + 260 + 'ms';
    });
  }

  function drawOrnament(host) {
    var box = host.getBoundingClientRect();
    var width = Math.ceil(box.width);
    var height = Math.ceil(box.height);
    if (!width) return;

    var isLattice = host.classList.contains('lattice');
    var svg = isLattice ? lattice(width, height) : register(width);

    host.textContent = '';
    host.appendChild(svg);
    host.classList.add('is-drawn');
    prime(svg, isLattice ? 34 : 26, isLattice ? 1500 : 900);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        svg.classList.add('is-going');
      });
    });
    host.brandDrawn = true;
  }

  /* Fills the mill swatches: the same weight of powder, ground two ways.
     Deterministic placement so both figures look the same on every load. */
  function fillMillSwatches(scope) {
    var seed = 7;
    function random() {
      seed = (seed * 1103515245 + 12345) % 2147483648;
      return seed / 2147483648;
    }

    scope.querySelectorAll('[data-mill-dots]').forEach(function (svg) {
      if (svg.childElementCount) return;

      var count = Number(svg.getAttribute('data-mill-dots')) || 100;
      var radius = Number(svg.getAttribute('data-mill-radius')) || 2.2;
      var size = 160;
      var pad = 10;
      var placed = [];
      var attempts = 0;

      while (placed.length < count && attempts < count * 400) {
        attempts++;
        var x = pad + random() * (size - pad * 2);
        var y = pad + random() * (size - pad * 2);
        var clear = placed.every(function (point) {
          var dx = x - point[0];
          var dy = y - point[1];
          return dx * dx + dy * dy > Math.pow(radius * 2.35, 2);
        });
        if (!clear) continue;
        placed.push([x, y]);
        svg.appendChild(
          node('circle', { cx: x.toFixed(1), cy: y.toFixed(1), r: radius })
        );
      }
    });
  }

  function observe(root) {
    var scope = root || document;

    fillMillSwatches(scope);

    if (!('IntersectionObserver' in window)) {
      scope.querySelectorAll('.reveal').forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    scope.querySelectorAll('.reveal.stagger').forEach(function (el) {
      [].forEach.call(el.children, function (child, index) {
        child.style.setProperty('--i', index);
      });
    });

    var revealer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealer.unobserve(entry.target);
        });
      },
      { threshold: 0.12 }
    );
    scope.querySelectorAll('.reveal').forEach(function (el) {
      revealer.observe(el);
    });

    if (reduceMotion) return;

    var inker = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || entry.target.brandDrawn) return;
          drawOrnament(entry.target);
          if (entry.target.brandDrawn) inker.unobserve(entry.target);
        });
      },
      { threshold: 0.05 }
    );
    scope.querySelectorAll('.register, .lattice').forEach(function (el) {
      inker.observe(el);
    });
  }

  function redrawOnResize() {
    var timer;
    window.addEventListener('resize', function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        document.querySelectorAll('.register.is-drawn, .lattice.is-drawn').forEach(function (host) {
          var box = host.getBoundingClientRect();
          if (!box.width) return;
          var isLattice = host.classList.contains('lattice');
          var svg = isLattice
            ? lattice(Math.ceil(box.width), Math.ceil(box.height))
            : register(Math.ceil(box.width));
          svg.classList.add('is-going');
          host.textContent = '';
          host.appendChild(svg);
        });
      }, 250);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      observe();
    });
  } else {
    observe();
  }
  redrawOnResize();

  document.addEventListener('shopify:section:load', function (event) {
    observe(event.target);
  });
})();
